/**
 * 图表 Tooltip「额外内容」富文本编辑器弹窗。
 * DOM 与选区细节见 `utils` 目录下 richEditor* 模块（富编辑：文本 + 字段标签）。
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'umi';
import { Button, ConfigProvider, Modal, Select } from 'tea-component';
import { IDimensionOption, ITooltipExtraItem } from './types';
import {
  consumeBackspaceSyntheticNewline,
  createFieldTagElement,
  extractSubmitItemsFromDom,
  getAdjacentFieldTag,
  getFieldItems,
  getFieldKey,
  insertFieldTagAtSavedOrCursor,
  insertNodeAtCursor,
  insertPlainNewlineAtCaret,
  mergeTextItems,
  renderEditorByItems,
  sanitizeMergedSubmitItems,
  setCaretAfterNode,
  setCaretBeforeNode,
} from './utils';
import './index.scss';
import SvgIcon from '@home/components/SvgIcon';

interface ITooltipExtraEditorModalProps {
  visible: boolean;
  /** 添加：展示「添加额外内容」；编辑：展示「编辑额外内容」 */
  mode?: 'add' | 'edit';
  tooltipExtra: ITooltipExtraItem[];
  tableGroups: Record<string, string>;
  dimensionOptions: IDimensionOption[];
  onClose: () => void;
  onConfirm: (items: ITooltipExtraItem[]) => void;
}

const TooltipExtraEditorModal: React.FC<ITooltipExtraEditorModalProps> = ({
  visible,
  mode = 'add',
  tooltipExtra,
  tableGroups,
  dimensionOptions,
  onClose,
  onConfirm,
}) => {
  // --- 国际化 ---
  const { locale, formatMessage } = useIntl();

  // --- 编辑器实例与同步标记（程序化改 DOM 时跳过 onInput 同步）---
  const editorRef = useRef<HTMLDivElement | null>(null);
  /** 打开「插入字段」下拉前保存的光标 Range，避免焦点被抢后丢失插入位置 */
  const savedEditorRangeRef = useRef<Range | null>(null);
  const [editorElement, setEditorElement] = useState<HTMLDivElement | null>(null);
  const syncEditorRef = useRef<boolean>(false);

  // --- 受控 UI 状态 ---
  /** Select 当前选中值（选完字段后会清空） */
  const [fieldValue, setFieldValue] = useState<string>();
  /** 与编辑器 DOM 同步的片段列表，用于禁用已选字段、空态等 */
  const [submitItems, setSubmitItems] = useState<ITooltipExtraItem[]>([]);

  /** 绑定 contentEditable 根节点：同时写入 ref 与 state，供打开弹窗时 effect 依赖 */
  const handleEditorRef = useCallback((node: HTMLDivElement | null): void => {
    editorRef.current = node;
    setEditorElement(node);
  }, []);

  /** 在点击插入字段区域（capture）时把当前选区存入 savedEditorRangeRef */
  const persistEditorSelectionToRef = useCallback((): void => {
    const editor = editorRef.current;
    const sel = window.getSelection();
    if (!editor || !sel || sel.rangeCount === 0) {
      return;
    }
    const range = sel.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) {
      return;
    }
    savedEditorRangeRef.current = range.cloneRange();
  }, []);

  /** 从编辑器 DOM 解析 ITooltipExtraItem[] 并写入 submitItems */
  const syncStateFromDom = useCallback((): void => {
    setSubmitItems(extractSubmitItemsFromDom(editorRef.current));
  }, []);

  /** 用数据模型重绘编辑器（打开/外部 tooltipExtra 变更时） */
  const renderEditorByItemsBound = useCallback((items: ITooltipExtraItem[]): void => {
    renderEditorByItems(editorRef.current, items, syncEditorRef);
  }, []);

  /** 弹窗打开：重置下拉与保存选区，合并初始数据并渲染编辑器 */
  useEffect(() => {
    if (!visible) {
      return;
    }
    setFieldValue(undefined);
    savedEditorRangeRef.current = null;
    const initialItems = tooltipExtra || [];
    const normalizedItems = sanitizeMergedSubmitItems(mergeTextItems(initialItems));
    setSubmitItems(normalizedItems);
    if (editorElement) {
      renderEditorByItemsBound(normalizedItems);
    }
  }, [visible, tooltipExtra, editorElement, renderEditorByItemsBound]);

  /** 弹窗可见期间：选区变化时持续更新 savedEditorRangeRef，保证下拉选字段仍能插回原光标 */
  useEffect(() => {
    if (!visible) {
      return;
    }
    const onSelectionChange = (): void => {
      const editor = editorRef.current;
      const sel = window.getSelection();
      if (!editor || !sel || sel.rangeCount === 0) {
        return;
      }
      const range = sel.getRangeAt(0);
      if (!editor.contains(range.commonAncestorContainer)) {
        return;
      }
      savedEditorRangeRef.current = range.cloneRange();
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, [visible]);

  /** 已插入字段的 key 集合，用于 Select 禁用与「已添加」展示 */
  const selectedFieldValues = useMemo<Set<string>>(() => {
    const fieldItems = getFieldItems(submitItems);
    return new Set(
      fieldItems.map((item) => getFieldKey(item)),
    );
  }, [submitItems]);

  /** 插入字段下拉的 options：已选字段 disabled + 文案提示 */
  const selectOptions = useMemo(() => {
    const addedText = formatMessage({ id: 'dbl_chart_tooltip_extra_added' });
    return dimensionOptions.map((item) => {
      const selected = selectedFieldValues.has(item.key);
      return {
        value: item.value,
        text: selected ? (
          <span className="dbl-chart-tooltip-extra-option-row">
            <span className="dbl-chart-tooltip-extra-option-label">
              {item.text}
            </span>
            <span className="dbl-chart-tooltip-extra-option-added">
              {addedText}
            </span>
          </span>
        ) : (
          item.text
        ),
        disabled: selected,
        groupKey: item.groupKey,
      };
    });
  }, [dimensionOptions, selectedFieldValues, formatMessage]);

  /** 在光标处插入任意节点（粘贴纯文本等），不做「仅换行占位」归一化 */
  const insertNodeAtCursorBound = useCallback((node: Node): void => {
    insertNodeAtCursor(editorRef.current, node);
  }, []);

  /**
   * 下拉选中维度字段：解析 option → 构建 field 项 → 下一帧恢复选区并插入字段标签 DOM，再 sync。
   * setTimeout(0)：等待 Select 关闭、焦点与选区稳定。
   */
  const handleInsertField = (nextFieldValue?: string): void => {
    if (!nextFieldValue) {
      return;
    }
    const option = dimensionOptions.find(
      (item: IDimensionOption) => item.value === nextFieldValue,
    );
    if (!option || selectedFieldValues.has(option.key)) {
      return;
    }
    const nextFieldItem: ITooltipExtraItem = {
      type: 'field',
      value: option.value,
      label: option.label,
      tableCode: option.tableCode,
      column: option.column,
      key: option.key,
    };
    window.setTimeout(() => {
      insertFieldTagAtSavedOrCursor(
        editorRef.current,
        savedEditorRangeRef.current,
        createFieldTagElement(nextFieldItem),
      );
      syncStateFromDom();
    }, 0);
    setFieldValue(undefined);
  };

  /** 清空编辑器与本地 submitItems，并重置下拉 */
  const handleClearAll = (): void => {
    setSubmitItems([]);
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
    }
    setFieldValue(undefined);
  };

  /** 用户输入导致 DOM 变化：同步到 submitItems（程序化 render 期间跳过） */
  const handleEditorInput = useCallback((): void => {
    if (syncEditorRef.current) {
      return;
    }
    syncStateFromDom();
  }, [syncStateFromDom]);

  /** 根据当前折叠选区，查找相邻（前/后）字段标签，供键盘与删除逻辑使用 */
  const getAdjacentFieldTagBound = useCallback((isBackward: boolean): HTMLElement | null => {
    return getAdjacentFieldTag(editorRef.current, isBackward);
  }, []);

  /**
   * 键盘：退格（合并删软换行 / 删相邻字段）、Delete、左右箭头绕标签、Enter 插文本换行。
   */
  const handleEditorKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Backspace') {
      if (event.nativeEvent.isComposing) {
        return;
      }
      const editorEl = editorRef.current;
      // 先处理「\n+ZWSP」一次退格删整段，避免按两次
      if (editorEl && consumeBackspaceSyntheticNewline(editorEl)) {
        event.preventDefault();
        syncStateFromDom();
        return;
      }
      const prevTag = getAdjacentFieldTagBound(true);
      if (prevTag) {
        event.preventDefault();
        prevTag.remove();
        syncStateFromDom();
      }
      return;
    }
    if (event.key === 'Delete') {
      const nextTag = getAdjacentFieldTagBound(false);
      if (nextTag) {
        event.preventDefault();
        nextTag.remove();
        syncStateFromDom();
      }
      return;
    }
    if (event.key === 'ArrowLeft') {
      const prevTag = getAdjacentFieldTagBound(true);
      if (prevTag) {
        event.preventDefault();
        setCaretBeforeNode(prevTag);
      }
      return;
    }
    if (event.key === 'ArrowRight') {
      const nextTag = getAdjacentFieldTagBound(false);
      if (nextTag) {
        event.preventDefault();
        setCaretAfterNode(nextTag);
      }
      return;
    }
    if (event.key !== 'Enter') {
      return;
    }
    // 输入法组字期间勿拦截，否则需多按一次 Enter
    if (event.nativeEvent.isComposing) {
      return;
    }
    event.preventDefault();
    insertPlainNewlineAtCaret(editorRef.current);
    syncStateFromDom();
    event.stopPropagation();
  }, [getAdjacentFieldTagBound, syncStateFromDom]);

  /** 仅粘贴纯文本，避免带入 HTML；在光标处插入文本节点 */
  const handleEditorPaste = useCallback((event: React.ClipboardEvent<HTMLDivElement>): void => {
    event.preventDefault();
    const pasteText = event.clipboardData.getData('text/plain');
    if (!pasteText) {
      return;
    }
    insertNodeAtCursorBound(document.createTextNode(pasteText));
    syncStateFromDom();
  }, [insertNodeAtCursorBound, syncStateFromDom]);

  /** 合并后是否视为空：控制占位符与「清空」按钮展示 */
  const isEditorEmpty = useMemo(
    () => sanitizeMergedSubmitItems(submitItems).length === 0,
    [submitItems],
  );

  /** 点击字段标签区域：按横坐标判断光标落在标签前或后 */
  const placeCaretByTagClick = useCallback((
    target: HTMLElement,
    clientX: number,
  ): boolean => {
    const fieldTag = target.closest('.dbl-chart-tooltip-extra-field-tag');
    if (!fieldTag) {
      return false;
    }
    const fieldTagRect = fieldTag.getBoundingClientRect();
    const isBefore = clientX < fieldTagRect.left + fieldTagRect.width / 2;
    if (isBefore) {
      setCaretBeforeNode(fieldTag);
    } else {
      setCaretAfterNode(fieldTag);
    }
    return true;
  }, []);

  /**
   * mousedown：点在标签上则预选光标位置（避免进标签内部）；点在 × 上则删标签。
   */
  const handleEditorMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>): void => {
    if (event.button !== 0) {
      return;
    }
    const target = event.target as HTMLElement;
    if (target?.dataset?.role !== 'remove-field-tag') {
      const fieldTag = target.closest('.dbl-chart-tooltip-extra-field-tag');
      if (!fieldTag) {
        return;
      }
      event.preventDefault();
      const fieldTagRect = fieldTag.getBoundingClientRect();
      const isBefore = event.clientX < fieldTagRect.left + fieldTagRect.width / 2;
      if (isBefore) {
        setCaretBeforeNode(fieldTag);
      } else {
        setCaretAfterNode(fieldTag);
      }
      return;
    }
    event.preventDefault();
    const fieldTag = target.closest('.dbl-chart-tooltip-extra-field-tag');
    if (!fieldTag) {
      return;
    }
    fieldTag.remove();
    syncStateFromDom();
  }, [syncStateFromDom]);

  /** click：与 placeCaretByTagClick 配合，避免默认选区跑进 contentEditable=false 内部 */
  const handleEditorClick = useCallback((event: React.MouseEvent<HTMLDivElement>): void => {
    if (event.button !== 0) {
      return;
    }
    const target = event.target as HTMLElement;
    if (target?.dataset?.role === 'remove-field-tag') {
      return;
    }
    const handled = placeCaretByTagClick(target, event.clientX);
    if (handled) {
      event.preventDefault();
    }
  }, [placeCaretByTagClick]);

  return (
    <Modal
      visible={visible}
      caption={formatMessage({
        id:
          mode === 'edit'
            ? 'dbl_chart_tooltip_extra_title_edit'
            : 'dbl_chart_tooltip_extra_title',
      })}
      className="dbl-chart-tooltip-extra-modal"
      onClose={onClose}
    >
      <Modal.Body>
        <div className="dbl-chart-tooltip-extra-modal-body">
          {/* 阻止点击冒泡到 Modal，避免误关 */}
          <div onClick={(event) => event.stopPropagation()}>
            <div className="dbl-chart-tooltip-extra-modal-input-row">
              {/* mousedown capture：在 Select 抢走焦点前保存编辑器选区 */}
              <div
                className="dbl-chart-tooltip-extra-modal-input-main"
                onMouseDownCapture={persistEditorSelectionToRef}
              >
                <ConfigProvider locale={locale === "en-US" ? "en" : "zh"}>
                  <Select
                    searchable={true}
                    listWidth={360}
                    button={formatMessage({
                      id: 'dbl_chart_tooltip_extra_insert_field',
                    })}
                    appearance="button"
                    className="dbl-chart-tooltip-extra-select"
                    overlayClassName='dbl-chart-tooltip-extra-select-overlay'
                    value={fieldValue}
                    groups={tableGroups}
                    options={selectOptions}
                    placeholder={formatMessage({
                      id: 'dbl_chart_tooltip_extra_insert_field',
                    })}
                    searchPlaceholder={formatMessage({
                      id: 'dbl_chart_tooltip_extra_search_field',
                    })}
                    onChange={(nextValue) => {
                      const value = nextValue as string;
                      setFieldValue(value);
                      handleInsertField(value);
                    }}
                  />
                </ConfigProvider>
              </div>
              {!isEditorEmpty ? (
                <div
                  className="dbl-chart-tooltip-extra-modal-actions"
                  onClick={handleClearAll}
                >
                  <SvgIcon type="datalab-clear" />
                  {formatMessage({
                    id: 'dbl_chart_tooltip_extra_clear_all',
                  })}
                </div>
              ) : null}
            </div>
            {/* data-placeholder / data-empty：由 SCSS 控制占位与空态 */}
            <div
              className="dbl-chart-tooltip-extra-modal-rich-editor"
              data-placeholder={formatMessage({
                id: 'dbl_chart_tooltip_extra_empty_tip',
              })}
              data-empty={isEditorEmpty ? 'true' : 'false'}
            >
              <div
                ref={handleEditorRef}
                className="dbl-chart-tooltip-extra-modal-rich-editor-content"
                contentEditable
                suppressContentEditableWarning
                onInput={handleEditorInput}
                onKeyDown={handleEditorKeyDown}
                onPaste={handleEditorPaste}
                onMouseDown={handleEditorMouseDown}
                onClick={handleEditorClick}
              />
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          onClick={() => {
            onConfirm(extractSubmitItemsFromDom(editorRef.current));
            onClose();
          }}
        >
          {formatMessage({ id: 'dbl_common_confirm' })}
        </Button>
        <Button type="weak" onClick={onClose}>
          {formatMessage({ id: 'dbl_common_cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TooltipExtraEditorModal;
